<template>
  <ReportWrapper>
    <template #actions>
      <CfsSelect
        v-model="formModel.documentType"
        :return-object="false"
        :items="UNFINALIZED_REPORT_DOCUMENT_TYPE_OPTIONS"
        placeholder="Document type"
      />
      <CfsDropdown
        btn-class="ml-auto"
        icon="mdi-tray-arrow-down"
        medium
        :disabled="!formModel.documentType"
        :loading="isLoading"
        color="primary"
        btn-width="32px"
        title="Download"
        offset-x
        :btn-type-text="false"
        :min-width="175"
        nudge-bottom="6px"
        rounded-menu="lg"
        :items="dropdownOptions"
      />
    </template>
  </ReportWrapper>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { useReports } from '@/composables/reports/useReports';
import { downloadFileByType } from '@/helpers/download';
import useForm from '@/composables/forms/useForm';
import { useToggle } from '@/composables/useToggle';
import {
  DocumentTypes,
  OutputTypes,
  UNFINALIZED_REPORT_DOCUMENT_TYPE_OPTION,
  UNFINALIZED_REPORT_DOCUMENT_TYPE_OPTIONS,
} from '@/constants/reports';
import { useToast } from '@/composables/useToast';
import date from '@/extensions/filters/date';
import ReportWrapper from '@/components/reports/ReportWrapper.vue';
import DownloadIcon from '@/assets/img/icons/download/download-with-circle.svg';

export default defineComponent({
  name: 'UnfinalizedInventory',
  components: {
    ReportWrapper,
  },
  setup() {
    const toast = useToast();
    const { formModel, resetFormModel } = useForm({
      documentType: null,
      outputType: OutputTypes.XLSX,
    });

    const { useDownloadInternalInventoryReport } = useReports();
    const { mutateAsync: downloadReport, isLoading } = useDownloadInternalInventoryReport();

    const download = async (outputType: string) => {
      if (!formModel.value.documentType) {
        toast.info('Please select document type.');
        return;
      }

      try {
        const { data } = await downloadReport({
          documentType: formModel.value.documentType,
          outputType,
        });

        if (!data) return;

        downloadFileByType(
          data,
          `${
            UNFINALIZED_REPORT_DOCUMENT_TYPE_OPTION[formModel.value.documentType as DocumentTypes]
              ?.description
          } Unfinalized Inventory ${date(new Date())}`,
          outputType
        );
      } catch (error) {
        console.error(error);
      }
    };

    const dropdownOptions = [
      {
        title: 'Download XLSX',
        icon: DownloadIcon,
        iconWidth: 20,
        handler: () => download(OutputTypes.XLSX),
      },
      {
        title: 'Download PDF',
        icon: DownloadIcon,
        iconWidth: 20,
        handler: () => download(OutputTypes.PDF),
      },
      {
        title: 'Download DOCX',
        icon: DownloadIcon,
        iconWidth: 20,
        handler: () => download(OutputTypes.DOCX),
      },
    ];

    const [filterMenu, toggleFilterMenu] = useToggle();

    const onResetFilters = () => {
      resetFormModel();
      onSubmit();
    };

    const onSubmit = async () => {
      onClose();
    };

    const onClose = () => {
      toggleFilterMenu(false);
    };

    return {
      filterMenu,
      formModel,
      onResetFilters,
      onSubmit,
      onClose,
      dropdownOptions,
      isLoading,
      UNFINALIZED_REPORT_DOCUMENT_TYPE_OPTIONS,
    };
  },
});
</script>
